/**
 * WorkshopPdfButton
 *
 * Per-row admin action that fetches the full order detail, renders the
 * WorkshopOrderPdfDocument for the primary line, and triggers a browser
 * download. Mirrors the lazy-fetch + disabled-while-loading + error-alert
 * pattern used by the Excel export sibling so the two icons feel the
 * same.
 *
 * Bundle: `@react-pdf/renderer` and the document component are imported
 * dynamically inside the click handler so admins who never click PDF pay
 * zero bundle cost on initial load.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { orderQueries } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

interface Props {
  orderId: string;
  /** Used to pick the primary (custom_insole / premium_3d_insole) line
   *  when the full detail loads. Falls back to the first line in the
   *  order so the workshop always gets something printable. */
  primaryLineId?: string | undefined;
}

/**
 * Fetch + render + download the workshop PDF for an order. Extracted from the
 * button's click handler VERBATIM so the mobile card menu (OrderTrackerPage,
 * Batch 4) triggers the exact same behavior; alerts on failure, resolves when
 * the download has been handed to the browser.
 */
export async function downloadWorkshopPdf(
  orderId: string,
  primaryLineId?: string | undefined,
): Promise<void> {
  try {
    const detail = await orderQueries
      .orderDetailQuery(supabase, orderId)
      .queryFn();
    if (!detail) {
      alert('Order not found.');
      return;
    }

    // Prefer the explicit primary line, otherwise the first custom
    // line, otherwise the first line of the order. The renderer
    // gracefully handles a missing `deep` (no modification cards), so
    // even a marketplace-only order won't crash the export.
    const targetLine =
      (primaryLineId ? detail.lines.find((l) => l.id === primaryLineId) : null) ??
      detail.lines.find(
        (l) => l.line_type === 'custom_insole' || l.line_type === 'premium_3d_insole',
      ) ??
      detail.lines[0];
    if (!targetLine) {
      alert('Order has no lines to export.');
      return;
    }

    // Dynamic import - keep the heavy pdfkit + fontkit bundle out of
    // the initial app payload.
    const [{ pdf }, { WorkshopOrderPdfDocument }] = await Promise.all([
      import('@react-pdf/renderer'),
      import('./WorkshopOrderPdfDocument'),
    ]);
    const blob = await pdf(
      <WorkshopOrderPdfDocument order={detail} line={targetLine} />,
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workshop-${detail.order_number}-${targetLine.line_number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after a short delay so the browser has time to start the
    // download against the blob URL.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    alert(
      `Workshop PDF export failed: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}

export function WorkshopPdfButton({ orderId, primaryLineId }: Props): React.ReactElement {
  const [busy, setBusy] = React.useState(false);

  const onClick = async (): Promise<void> => {
    setBusy(true);
    try {
      await downloadWorkshopPdf(orderId, primaryLineId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      title="Workshop PDF"
      disabled={busy}
      onClick={onClick}
      className="inline-flex items-center justify-center h-7 w-7 max-sm:h-10 max-sm:w-10 rounded-md text-text-muted hover:text-brand-700 hover:bg-brand-50 disabled:opacity-30"
      aria-label="Download workshop PDF"
    >
      <FileText className="h-4 w-4" />
    </button>
  );
}
